import React, { useEffect, useState } from 'react';
import { Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, Icon, IconButton, Input, Stack, Box, Typography, Grid, Avatar, TextField, CardHeader } from "@mui/material";
import { Button } from "../atoms";
import { useSetSnackBar } from "../../store/Snackbar.state";
import { GoldIcon, TurkishFlagIcon } from '../../assets/icons';
import StorefrontTwoToneIcon from '@mui/icons-material/StorefrontTwoTone';
import { useGetMerchantGoldBalance, useGetMerchantSetGold } from '../../hooks';
import dayjs from 'dayjs';

interface Props {
    merchantId: string;
    goldBindingOpen: boolean;
    setGoldBindingOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GoldBindingModal: React.FC<Props> = ({ merchantId, goldBindingOpen, setGoldBindingOpen }) => {

    const { mutate: getMerchantGoldBalance, isLoading: isGetMerchantGoldBalanceLoading } = useGetMerchantGoldBalance();
    const { mutate: setGoldBalance, isLoading: isSetGoldBalanceLoading } = useGetMerchantSetGold();
    const setSnackbar = useSetSnackBar();

    const [goldBalanceData, setGoldBalanceData] = useState({
        balance: 0,
        goldRate: 0,
        maxGoldQuantity: 0
    });

    const [goldQuantity, setGoldQuantity] = useState<string>('');
    const [tryAmount, setTryAmount] = useState<string>('');

    useEffect(() => {
        if (goldBindingOpen) {
            getMerchantGoldBalance(undefined, {
                onSuccess: (data) => {
                    setGoldBalanceData({
                        balance: data?.data.balance,
                        goldRate: data?.data.goldRate,
                        maxGoldQuantity: data?.data.maxGoldQuantity
                    });
                },
                onError: (error) => {
                    setSnackbar({
                        severity: "error",
                        description: error?.message || "Bir hata oluştu",
                        isOpen: true,
                    })
                }
            });
        }
    }, [merchantId, goldBindingOpen]);

    const handleGoldBinding = () => {
        if (!goldQuantity || parseFloat(goldQuantity) <= 0) {
            setSnackbar({
                severity: "error",
                description: "Lütfen geçerli bir altın miktarı giriniz",
                isOpen: true,
            });
            return;
        }

        setGoldBalance(
            {
                tlTutar: parseFloat(tryAmount),
            },
            {
                onSuccess: (response: any) => {
                    if (response.isSuccess) {
                        setSnackbar({
                            severity: "success",
                            description: response?.data.unit + " GR Altın bağlama işlemi başarıyla gerçekleştirildi",
                            isOpen: true,
                        });
                        setGoldQuantity('');
                        setTryAmount('');
                        setGoldBindingOpen(false);
                    } else {
                        setSnackbar({
                            severity: "error",
                            description: response.message || "İşlem sırasında bir hata oluştu",
                            isOpen: true,
                        });
                    }
                },
                onError: () => {
                    setSnackbar({
                        severity: "error",
                        description: "İşlem sırasında bir hata oluştu",
                        isOpen: true,
                    });
                },
            }
        );
    };

    return (
        <Dialog open={goldBindingOpen} onClose={() => setGoldBindingOpen(false)} maxWidth="sm" fullWidth={true}>
            <DialogTitle>Altın Bağlama İşlemleri</DialogTitle>
            <Divider sx={{
                borderColor: '#05AA3C',
                borderWidth: '1.5px'
            }} />
            <DialogContent>
                <DialogContentText>
                    <Stack sx={{
                        border: 1,
                        borderColor: 'grey.300',
                        borderRadius: 2,
                        padding: '15px'
                    }} direction="row" alignContent={'center'} justifyContent={'center'} spacing={1}>
                        <StorefrontTwoToneIcon sx={{ color: '#05AA3C' }} />
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: '600', alignContent: 'center', color: 'black' }}>{localStorage.getItem("merchantName")}</Typography>
                    </Stack>
                    <Stack direction={'row'} spacing={1} marginTop={5}>
                        <Card variant="outlined" sx={{ borderRadius: 3, width: '100%' }}>
                            <CardContent sx={{
                                alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 1
                            }}>
                                <Typography variant="body2" color="text.secondary">Toplam Hesap Bakiyesi</Typography>
                                <Typography variant="body2" color="text.primary">
                                    {parseFloat(goldBalanceData?.balance?.toString() || '0').toLocaleString('tr-TR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })} ₺
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ borderRadius: 3, width: '100%' }}>
                            <CardContent sx={{
                                alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 1
                            }}>
                                <Typography variant="body2" color="text.secondary">Hesaplanan Altın Miktarı</Typography>
                                <Typography variant="body2" color="text.primary">{parseFloat(goldBalanceData?.maxGoldQuantity?.toString() || '0').toLocaleString('tr-TR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })} Gr XAU</Typography>
                            </CardContent>
                        </Card>
                    </Stack>
                    <Divider sx={{
                        marginTop: '20px',
                        marginBottom: '20px',
                        borderColor: '#05AA3C'
                    }} />
                    <Stack direction={'row'} spacing={1}>
                        <Card variant="outlined" sx={{ borderRadius: 3, p: 2, width: '100%' }}>
                            <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={2}>
                                        <Avatar sx={{ bgcolor: 'transparent' }}>
                                            <GoldIcon />
                                        </Avatar>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle1" fontWeight="bold">Altın</Typography>
                                        <Typography variant="body2" color="text.secondary">Gram</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField
                                            variant="standard"
                                            id='goldQuantity'
                                            value={goldQuantity}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setGoldQuantity(value);
                                                // Calculate TRY amount based on gold rate
                                                const numValue = parseFloat(value) || 0;
                                                const calculatedTryAmount = numValue * (goldBalanceData?.goldRate || 0);
                                                setTryAmount(calculatedTryAmount.toString());
                                            }}
                                            type="number"
                                            placeholder="0.00"
                                            InputProps={{
                                                disableUnderline: true,
                                                endAdornment: <Typography variant="body2" color="text.secondary">Gr</Typography>,
                                                sx: { textAlign: 'right' }
                                            }}
                                            inputProps={{
                                                min: 0,
                                                max: goldBalanceData?.maxGoldQuantity || 0,
                                                step: 0.01,
                                                style: { textAlign: 'right', fontSize: '1rem', color: 'rgba(0, 0, 0, 0.6)' }
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>

                                {_RenderGoldPrice({
                                    goldBalanceData: goldBalanceData
                                })}

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={2}>
                                        <Avatar sx={{ bgcolor: 'transparent' }}>
                                            <TurkishFlagIcon />
                                        </Avatar>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle1" fontWeight="bold">TRY</Typography>
                                        <Typography variant="body2" color="text.secondary">Türk Lirası</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField
                                            variant="standard"
                                            id='tryAmount'
                                            value={tryAmount}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setTryAmount(value);
                                                // Calculate gold quantity based on TRY amount
                                                const numValue = parseFloat(value) || 0;
                                                const calculatedGoldQuantity = numValue / (goldBalanceData?.goldRate || 1);
                                                setGoldQuantity(calculatedGoldQuantity.toString());
                                            }}
                                            type="number"
                                            placeholder="0.00"
                                            InputProps={{
                                                disableUnderline: true,
                                                endAdornment: <Typography variant="body2" color="text.secondary">₺</Typography>,
                                                sx: { textAlign: 'right' }
                                            }}
                                            inputProps={{
                                                min: 0,
                                                max: (goldBalanceData?.maxGoldQuantity || 0) * (goldBalanceData?.goldRate || 0),
                                                step: 0.01,
                                                style: { textAlign: 'right', fontSize: '1rem', color: 'rgba(0, 0, 0, 0.6)' }
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Stack>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    text='Bağlantı Emri Ver'
                    onClick={handleGoldBinding}
                    variant='contained'
                    disabled={isSetGoldBalanceLoading || !goldQuantity || parseFloat(goldQuantity) <= 0}
                    sx={{
                        color: 'white',
                        backgroundColor: '#05AA3C',
                        '&:disabled': {
                            backgroundColor: 'rgba(5, 170, 60, 0.5)',
                            color: 'white'
                        }
                    }}
                >
                    {isSetGoldBalanceLoading ? 'İşlem Yapılıyor...' : 'Bağlantı Emri Ver'}
                </Button>
                <Button text='İptal' onClick={() => setGoldBindingOpen(false)} color="error" variant='outlined'>
                    İptal
                </Button>
            </DialogActions>
        </Dialog >
    );
}

function _RenderGoldPrice({ goldBalanceData }: { goldBalanceData: any }) {
    const goldRate = parseFloat(goldBalanceData?.goldRate?.toString() || '0');

    return (
        <Box sx={{ position: 'relative', my: 4, textAlign: 'center' }}>
            {/* Horizontal line */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: '#ccc',
                    zIndex: 0
                }}
            />

            {/* Date centered on the line */}
            <Box
                sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    px: 1,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: '#666',
                    zIndex: 5,
                }}
            >
                {dayjs().format('DD.MM.YYYY HH:mm:ss')}
            </Box>

            {/* Gold price box */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    backgroundColor: 'white',
                    display: 'inline-block',
                    textAlign: 'center',
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    p: 2,
                    mx: 'auto',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                }}
            >
                1 Gram Altın ={' '}
                {goldRate.toLocaleString('tr-TR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}{' '}
                ₺
            </Box>
        </Box>
    );
}